import React, { useState } from 'react';

// Types
import {
  FormTypeType,
  ReviewFormTemplateType,
  RatingQuestionType,
  RatingOptionType,
} from 'Shared/types/ReviewFormTemplate.type';

// Hooks
import { useQuestion } from './use-question';

// Components
import { WpButton } from 'Wpds/components/button';
import QuestionWrapper from './QuestionWrapper';
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpListSelect } from 'Wpds/components/list';
import QuestionExplanation from './QuestionExplanation';

// Styles
import './RatingQuestion.scss';

interface ValueOptionProps extends RatingOptionType {
  handleUpdateOptions: Function;
  handleDeleteOption?: Function;
}

const ValueOption = ({ number, label, handleUpdateOptions, handleDeleteOption }: ValueOptionProps) => {
  const [labelValue, setLabelValue] = useState(label);

  const handleUpdateLabel = value => {
    setLabelValue(value);
    handleUpdateOptions({ number, label: value });
  };

  const handleDeleteOptionClick = () => {
    handleDeleteOption({ number });
  };

  const baseClassName = 'RatingQuestion';

  return (
    <div className={`${baseClassName}__values__value`}>
      <div className={`${baseClassName}__values__value__number`}>{number}</div>
      <div className={`${baseClassName}__values__value__label`}>
        <WpTextField
          id="label-input"
          label="label"
          value={labelValue}
          labelHidden
          placeholder="Label"
          onChange={handleUpdateLabel}
          max={30}
        />
      </div>

      {handleDeleteOption && (
        <div className={`${baseClassName}__values__value__actions`} onClick={handleDeleteOptionClick}>
          <WpButton variant="tertiary">
            <WpIcon name="trash" />
          </WpButton>
        </div>
      )}
    </div>
  );
};

type Props = {
  question: RatingQuestionType;
  onChange: Function;
  handleDeleteQuestion: Function;
  handleDuplicateQuestion: Function;
  activeQuestionKey: string;
  setActiveQuestionKey: Function;
  handleMoveQuestion: Function;
  reviewFormType: FormTypeType;
  reviewFormTemplates: ReviewFormTemplateType[];
};

const RatingQuestion = ({
  question,
  onChange,
  handleDuplicateQuestion,
  handleDeleteQuestion,
  activeQuestionKey,
  setActiveQuestionKey,
  handleMoveQuestion,
  reviewFormType,
  reviewFormTemplates,
}: Props) => {
  const { handleUpdateQuestion } = useQuestion({ question, onChange });

  const isActive = activeQuestionKey === question.key;
  const values = question.values;

  const options = values.map(value => {
    return {
      value: value.number.toString(),
      label: value.number.toString(),
      tooltip: value.label,
    };
  });

  const handleUpdateOptions = (option: RatingOptionType) => {
    const newValues = values;
    const index = newValues.findIndex(v => v.number === option.number);
    newValues[index] = option;
    // const newV = { ...question, questions };

    handleUpdateQuestion({ values: newValues });
  };

  const handleAddOption = () => {
    handleUpdateQuestion({ values: [...values, { number: values.length + 1, label: '' }] });
  };

  const handleDeleteOption = ({ number }: { number: number }) => {
    handleUpdateQuestion({ values: values.filter(v => v.number !== number) });
  };

  const baseClassName = 'RatingQuestion';

  return (
    <QuestionWrapper
      question={question}
      onChange={handleUpdateQuestion}
      handleDeleteQuestion={handleDeleteQuestion}
      activeQuestionKey={activeQuestionKey}
      setActiveQuestionKey={setActiveQuestionKey}
      handleDuplicateQuestion={handleDuplicateQuestion}
      handleMoveQuestion={handleMoveQuestion}
      reviewFormType={reviewFormType}
      reviewFormTemplates={reviewFormTemplates}
    >
      {isActive ? (
        <div className={baseClassName}>
          <div className={`${baseClassName}__values`}>
            {values.map(value => (
              <ValueOption
                number={value.number}
                label={value.label}
                key={`${value.number}_rating_question`}
                handleUpdateOptions={handleUpdateOptions}
                handleDeleteOption={value.number > 2 && value.number > values.length - 1 && handleDeleteOption}
              />
            ))}
            {values.length < 10 && (
              <WpButton variant="tertiary" onClick={handleAddOption}>
                <WpIcon name="plus" /> Add option
              </WpButton>
            )}
          </div>
          <QuestionExplanation question={question} onChange={onChange} />
        </div>
      ) : (
        <div className={baseClassName}>
          <div className={`${baseClassName}__not-active`}>
            <WpListSelect
              id={`${question.key}-list-example`}
              options={options}
              direction="horizontal"
              max={1}
              showIcon={false}
              selectedOptions={[]}
              disableOnMax={false}
            />
            {question.showExplanation && (
              <div className={`${baseClassName}__not-active__explanation`}>
                <p>{question.explanationDescription}</p>
                <WpTextField
                  id="rating-explanation-placeholder"
                  label="explanation"
                  placeholder="Short Answer"
                  labelHidden
                  readOnly
                  multiline={2}
                />
              </div>
            )}
          </div>
        </div>
      )}
    </QuestionWrapper>
  );
};

export default RatingQuestion;
